import type { Assessment, CaseInconsistency } from "@/services/api/case-checking";

import { inconsistencyRollupStatus } from "./inconsistencyRollup";

/** Structural minimum needed to read rule linkage. Accepts new + legacy shapes. */
export interface RuleLinkable {
  rule_ids?: string[] | null;
  rule_id?: string | null;
}

/** Read the (possibly legacy) rule ID list off a remedial action. */
export function ruleIdsFor(action: RuleLinkable): string[] {
  if (action.rule_ids && action.rule_ids.length > 0) return action.rule_ids;
  return action.rule_id ? [action.rule_id] : [];
}

export interface RuleCoverageGap {
  rule_id: string;
  status: "failed" | "unclear";
  group_id: string;
  label: string;
}

function normalizeRuleId(id: string): string {
  return id.trim().toLowerCase();
}

/**
 * Rules the LLM was expected to address but didn't: any failed rule with no linked remedial,
 * plus unclear rules with no linked remedial. Callers can filter by severity.
 */
export function findRuleCoverageGaps(
  assessment: Assessment | null | undefined,
  actions: readonly (RuleLinkable | undefined | null)[],
  inconsistencies?: readonly CaseInconsistency[] | null,
): RuleCoverageGap[] {
  const covered = new Set<string>();
  for (const a of actions) {
    if (!a) continue;
    for (const id of ruleIdsFor(a)) covered.add(normalizeRuleId(id));
  }
  const gaps: RuleCoverageGap[] = [];
  const gapRuleIds = new Set<string>();
  for (const group of assessment?.groups ?? []) {
    for (const rule of group.rules ?? []) {
      if (rule.status !== "failed" && rule.status !== "unclear") continue;
      const normalizedId = normalizeRuleId(rule.rule_id);
      if (covered.has(normalizedId)) continue;
      gapRuleIds.add(normalizedId);
      gaps.push({
        rule_id: rule.rule_id,
        status: rule.status,
        group_id: group.group_id,
        label: rule.label || rule.rule_id,
      });
    }
  }
  if (inconsistencies) {
    for (const row of inconsistencies) {
      const checkId = (row.check_id ?? row.field ?? "").trim();
      if (!checkId) continue;
      if (checkId.toUpperCase() === "BACKSTOP") continue;
      const rollupStatus = inconsistencyRollupStatus(row);
      if (rollupStatus !== "fail" && rollupStatus !== "unclear") continue;
      const normalizedCheckId = normalizeRuleId(checkId);
      if (covered.has(normalizedCheckId) || gapRuleIds.has(normalizedCheckId)) continue;
      gapRuleIds.add(normalizedCheckId);
      gaps.push({
        rule_id: checkId,
        status: rollupStatus === "fail" ? "failed" : "unclear",
        group_id: row.area || "inconsistencies",
        label: row.field || row.check_id || checkId,
      });
    }
  }
  return gaps;
}
